/**
 * Auth bootstrap state + token store.
 *
 * - Access token lives in memory only (never localStorage).
 * - Refresh token is an HttpOnly cookie (`kreart_refresh`) scoped to /api/v1/auth.
 * - Cross-tab login/logout broadcasts keep sibling tabs in sync.
 * - Concurrent refresh within a tab share one in-flight promise.
 * - Cross-tab refresh races are serialised via navigator.locks when available.
 */
import type { AuthUser } from '@kreart/shared';

export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

export interface AuthSnapshot {
  status: AuthStatus;
  user: AuthUser | null;
  accessToken: string | null;
  accessTokenExpiresAt: number | null;
}

type Listener = (snap: AuthSnapshot) => void;

const CHANNEL_NAME = 'kreart-auth';
type AuthBroadcast =
  | { type: 'login'; user: AuthUser; accessToken: string; accessTokenExpiresAt: number }
  | { type: 'logout' }
  | { type: 'token-rotated'; accessToken: string; accessTokenExpiresAt: number };

class AuthStore {
  private state: AuthSnapshot = {
    status: 'unknown',
    user: null,
    accessToken: null,
    accessTokenExpiresAt: null,
  };

  private listeners = new Set<Listener>();
  private channel: BroadcastChannel | null = null;

  constructor() {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.addEventListener('message', (event) => this.handleBroadcast(event.data));
    }
  }

  get snapshot(): AuthSnapshot {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  setAuthenticated(
    user: AuthUser,
    accessToken: string,
    accessTokenExpiresAt: string | number,
  ): void {
    const expiresAt =
      typeof accessTokenExpiresAt === 'string'
        ? Date.parse(accessTokenExpiresAt)
        : accessTokenExpiresAt;
    this.setState({ status: 'authenticated', user, accessToken, accessTokenExpiresAt: expiresAt });
    this.broadcast({ type: 'login', user, accessToken, accessTokenExpiresAt: expiresAt });
  }

  setTokenRotated(accessToken: string, accessTokenExpiresAt: string | number): void {
    const expiresAt =
      typeof accessTokenExpiresAt === 'string'
        ? Date.parse(accessTokenExpiresAt)
        : accessTokenExpiresAt;
    if (this.state.status !== 'authenticated') return;
    this.setState({ ...this.state, accessToken, accessTokenExpiresAt: expiresAt });
    this.broadcast({ type: 'token-rotated', accessToken, accessTokenExpiresAt: expiresAt });
  }

  setUnauthenticated(options: { broadcast?: boolean } = {}): void {
    const wasAuthed = this.state.status === 'authenticated';
    this.setState({
      status: 'unauthenticated',
      user: null,
      accessToken: null,
      accessTokenExpiresAt: null,
    });
    if (options.broadcast !== false && wasAuthed) {
      this.broadcast({ type: 'logout' });
    }
  }

  private setState(next: AuthSnapshot): void {
    this.state = next;
    for (const l of this.listeners) l(next);
  }

  private broadcast(msg: AuthBroadcast): void {
    try {
      this.channel?.postMessage(msg);
    } catch {
      /* noop */
    }
  }

  private handleBroadcast(msg: AuthBroadcast): void {
    if (msg.type === 'login') {
      this.setState({
        status: 'authenticated',
        user: msg.user,
        accessToken: msg.accessToken,
        accessTokenExpiresAt: msg.accessTokenExpiresAt,
      });
    } else if (msg.type === 'logout') {
      this.setState({
        status: 'unauthenticated',
        user: null,
        accessToken: null,
        accessTokenExpiresAt: null,
      });
    } else if (msg.type === 'token-rotated' && this.state.status === 'authenticated') {
      this.setState({
        ...this.state,
        accessToken: msg.accessToken,
        accessTokenExpiresAt: msg.accessTokenExpiresAt,
      });
    }
  }
}

export const authStore = new AuthStore();
